// src/components/TeamDetailsDialog.jsx
import React, { useEffect, useState } from "react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { useNavigate } from "react-router-dom";
import { teamKey } from "../utils/teamKey";
import { Copy, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamDetailsDialog({ open, onClose, team }) {
  const [teamStats, setTeamStats] = useState(null);
  const [kopiert, setKopiert] = useState(false);
  const navigate = useNavigate();

  // Der Link ist nur so lange gueltig, wie das Team existiert – er wird aus dem
  // share_token gebaut, den das Backend beim Anlegen vergibt. Teams aus der Zeit
  // davor haben keinen; dort erscheint stattdessen ein Hinweis.
  const teamLink = team?.share_token
    ? `${window.location.origin}/team/${team.share_token}`
    : null;

  const linkKopieren = async () => {
    if (!teamLink) return;
    try {
      await navigator.clipboard.writeText(teamLink);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    } catch (err) {
      // clipboard schlaegt ohne HTTPS oder ohne Nutzerfreigabe fehl. Der Link
      // steht daneben im Klartext und laesst sich dann von Hand markieren.
      console.warn("Kopieren nicht moeglich:", err);
    }
  };

  useEffect(() => {
    if (!open || !team) return;

    async function loadStats() {
      try {
        const results = await fetchWithAuth(`${BACKEND_URL}/results`, navigate);
        // Passendes Team im "teams"-Array suchen – ueber die stabile Team-ID,
        // nicht ueber den Namen (Rueckfall fuer Ranking-Dokumente von vorher).
        const found = results.teams.find(
          (t) => (t.team_id ?? t.team_name) === teamKey(team)
        );
        setTeamStats(found || null);
      } catch (err) {
        console.error("Fehler beim Laden der Ergebnisse:", err);
        setTeamStats(null);
      }
    }

    loadStats();
    setKopiert(false);
  }, [open, team, navigate]);

  if (!open || !team) return null;

  const showAges = team.event_type === "Kinder";

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-secondary-950 bg-opacity-75 px-4 z-50">
      <div className="bg-secondary-900 p-6 rounded shadow-lg w-full max-w-md text-white border border-secondary-800">
        <h2 className="text-xl font-bold mb-4 break-words text-white">
          {team.team_name}
        </h2>

        <p className="mb-2 text-gray-400">
          Kategorie: {team.event_type} | Typ: {team.team_type || "-"}
        </p>

        {team.contact_email && (
          <p className="mb-3 text-gray-400 break-words">
            <span className="font-semibold text-white">Kontakt:</span>{" "}
            <a
              href={`mailto:${team.contact_email}`}
              className="text-primary-400 hover:underline"
            >
              {team.contact_email}
            </a>
          </p>
        )}

        {team.contact_phone && (
          <p className="mb-3 text-gray-400 break-words">
            <span className="font-semibold text-white">Handy:</span>{" "}
            <a
              href={`tel:${team.contact_phone}`}
              className="text-primary-400 hover:underline"
            >
              {team.contact_phone}
            </a>
          </p>
        )}

        <h3 className="font-semibold mb-1 text-white">Mitglieder</h3>
        <ul className="list-disc list-inside mb-4 text-gray-400">
          {["member1", "member2", "member3", "member4", "member5"]
            .filter((m) => team[m])
            .map((m, i) => (
              <li key={i}>
                {team[m]}
                {showAges && team[`${m}_age`] && (
                  <> ({team[`${m}_age`]} Jahre)</>
                )}
              </li>
            ))}
        </ul>

        {showAges && (
          <p className="mb-2 text-gray-400">
            <span className="font-semibold">Durchschnittsalter:</span>{" "}
            {team.average_age ?? "-"}
          </p>
        )}

        {teamStats ? (
          <div className="mb-4 text-gray-400 space-y-1">
            <p>
              <span className="font-semibold text-white">Gesamtpunkte:</span>{" "}
              <span className="text-primary-500 font-bold">
                {teamStats.total_points}
              </span>
            </p>
            <p>
              <span className="font-semibold text-white">
                Stationen erledigt:
              </span>{" "}
              {teamStats.stations_completed}
            </p>
            {showAges && (
              <p>
                <span className="font-semibold text-white">Altersbonus:</span>{" "}
                {teamStats.age_bonus}
              </p>
            )}
          </div>
        ) : (
          <p className="mb-4 text-gray-500">Lade Ergebnisse…</p>
        )}

        {/* Persoenlicher Team-Link. Der Schluessel darin ist das einzige
            Zugangsmerkmal zur oeffentlichen Team-Ansicht – er gehoert deshalb
            nur an das jeweilige Team, nicht auf einen Aushang. */}
        <div className="mb-4 border-t border-secondary-800 pt-3">
          <h3 className="font-semibold mb-1 text-white">Ergebnis-Link fürs Team</h3>
          {teamLink ? (
            <>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate text-xs bg-secondary-800 text-gray-300 px-2 py-2 rounded">
                  {teamLink}
                </code>
                <button
                  onClick={linkKopieren}
                  className="flex-shrink-0 px-3 py-2 rounded bg-secondary-700 hover:bg-secondary-600 text-gray-200 transition-colors"
                  title="Link kopieren"
                  aria-label="Team-Link kopieren"
                >
                  {kopiert ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Zeigt diesem Team seine eigenen Ergebnisse – ohne Platzierung und
                ohne andere Teams.
              </p>
            </>
          ) : (
            <p className="text-xs text-gray-500">
              Noch kein Link vorhanden. Er entsteht beim nächsten Speichern des
              Teams.
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-secondary-700 hover:bg-secondary-600 text-gray-200 transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
